import RichTextFullWidth from "@components/C16_RichTextFullWidth/C16_RichTextFullWidth";
import Cta from "@components/Cta/Cta";
import NextContentfulImage from "@components/Image/NextContentfulImage";
import { getVideoUrl } from "@components/VideoPlayer/utils";
import { HEADER_BANNER_ANCHOR } from "@utilities/e2e/e2eAnchorsList";
import { getE2EAnchorAttribute } from "@utilities/e2e/getE2EAnchor";
import richTextify, { transformStylesInRichText } from "@utilities/richtext";
import clsx from "clsx";
import { useCallback, useRef, useState } from "react";
import { useInView } from "react-hook-inview";
import {
  getImagePanelStyle,
  getImageSize,
  getImageSizes,
  getImageStyle,
} from "./C46_HeaderBanner.functions";
import { HeaderBannerProps } from "./C46_HeaderBanner.interfaces";
import styles from "./C46_HeaderBanner.module.scss";
import C46_HeaderLightBoxVideoPlayer from "./C46_HeaderLightBoxVideoPlayer";
import C46_HeaderVideoPlayer from "./C46_HeaderVideoPlayer";

const HeaderBanner = (props: { content: HeaderBannerProps }) => {
  const { content } = props;

  const {
    title,
    isLight,
    localVideo,
    description,
    descriptionEnhanced,
    descriptionEnhancedStyle,
    showChevron,
    ctaLink,
    ctaLinkAppearance,
    ctaSecondaryLink,
    desktopImage,
    bannerType,
    videoLinkText,
    secondaryTitle,
    isVimeoCode,
    videoCode,
    hideLine,
    subDescription,
    videoDescription,
  } = content;

  const bannerContent = useRef<HTMLDivElement | null>(null);
  const [hideImage, setHideImage] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);

  const [ref, isVisible] = useInView({
    threshold: 0,
    unobserveOnEnter: true,
  });

  const handleVideoPlay = useCallback(() => {
    setHideImage(true);
  }, []);

  const handleVideoEnded = useCallback(() => {
    setHideImage(false);
  }, []);

  const scrollDown = (event) => {
    event.preventDefault();
    const rect = bannerContent?.current?.getBoundingClientRect();

    window.scroll({
      top:
        rect.top +
        rect.height +
        (window.scrollY || document.documentElement.scrollTop),
      left: 0,
      behavior: "smooth",
    });
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      scrollDown(event);
    }
  };

  const handleOpenLightbox = () => {
    setShowLightbox(true);
  };

  const handleCloseLightbox = () => {
    setTimeout(() => {
      setShowLightbox(false);
    }, 300);
  };

  const videoUrl = getVideoUrl(isVimeoCode ? "vimeo" : "youtube", videoCode);
  const bannerVideoUrl = localVideo?.fields.file.url || videoUrl;
  const lightboxVideoUrl = videoUrl || localVideo?.fields.file.url;

  const isVideoBackground = bannerType === "Video Background";
  const isVideoOverlay = bannerType === "Video Overlay";
  const isImage = bannerType === "Image";

  const hasVideoCta = videoLinkText && isVideoOverlay;
  const hasPrimaryCta = ctaLink && ctaLink.linkText && ctaLink.url;
  const hasSecondaryCta =
    ctaSecondaryLink && ctaSecondaryLink.linkText && ctaSecondaryLink.url;
  const hasAnyCta = hasVideoCta || hasPrimaryCta || hasSecondaryCta;

  const imagePanelStyle = getImagePanelStyle(bannerType);

  return (
    <div
      ref={ref}
      className={clsx(
        styles["header-banner-image"],
        styles["flush"],
        !desktopImage && styles["no-image"],
        !desktopImage && styles["full-width"],
        desktopImage && isVideoBackground && styles["page-video"],
        desktopImage && isVideoOverlay && styles["type-one"],
        desktopImage && isImage && styles["type-three"],
        hideImage && styles["played"],
        isVisible && styles["animate"],
        !isLight &&
          (typeof window === "object" && window?.location?.pathname === "/"
            ? styles["blue-main-page"]
            : styles["blue"]),
        hideLine && styles["hide-line"],
        descriptionEnhancedStyle
      )}
    >
      <div
        ref={bannerContent}
        className={clsx(styles["main-content"], { wrapper: isImage })}
      >
        {desktopImage && (
          <div
            className={clsx(styles["image-wrapper"], "chromatic-ignore", {
              "flush-no-padding": !isImage,
            })}
          >
            <div className={styles["inner-wrapper"]}>
              {isVideoBackground && bannerVideoUrl && (
                <div
                  className={styles["video-player-wrapper"]}
                  style={imagePanelStyle}
                >
                  <C46_HeaderVideoPlayer
                    url={bannerVideoUrl}
                    onVideoReady={handleVideoPlay}
                    onVideoPlay={handleVideoPlay}
                    onVideoEnded={handleVideoEnded}
                    videoDescription={videoDescription}
                  />
                </div>
              )}
              <div
                className={styles["picture-wrapper"]}
                style={imagePanelStyle}
              >
                <NextContentfulImage
                  src={desktopImage.fields.url}
                  alt={desktopImage.fields.altText || ""}
                  {...getImageSize(bannerType)}
                  style={getImageStyle(bannerType)}
                  sizes={getImageSizes(bannerType)}
                  preload
                  fetchPriority="high"
                />
              </div>
            </div>
            {!hideLine && (
              <span className={clsx(styles.line, styles.image)}>
                <span className={styles["inner-line"]} />
              </span>
            )}
          </div>
        )}
        {isVideoBackground && showChevron && (
          <button
            type="button"
            className={styles["slide-down"]}
            onClick={scrollDown}
            onKeyDown={handleKeyPress}
            aria-label="Scroll down"
          >
            <span className={styles.chevron} />
          </button>
        )}
        <div className={clsx(styles["text-with-line"], { wrapper: !isImage })}>
          <div className={styles["content-wrapper"]}>
            <div className={styles.content}>
              {isVideoBackground ? (
                <>
                  {title && title.trim().startsWith("<h1") ? (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: transformStylesInRichText(title),
                      }}
                    />
                  ) : (
                    <h1
                      {...getE2EAnchorAttribute(
                        HEADER_BANNER_ANCHOR,
                        "header-title"
                      )}
                      className={secondaryTitle && styles["small-page-title"]}
                      dangerouslySetInnerHTML={{
                        __html: transformStylesInRichText(title),
                      }}
                    />
                  )}
                  {secondaryTitle && <h2>{secondaryTitle}</h2>}
                </>
              ) : (
                <>
                  <span className={styles["section-label"]}>
                    {secondaryTitle}
                  </span>
                  {title && title.startsWith("<h1") ? (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: transformStylesInRichText(title),
                      }}
                    />
                  ) : (
                    <h1
                      {...getE2EAnchorAttribute(
                        HEADER_BANNER_ANCHOR,
                        "header-title"
                      )}
                      dangerouslySetInnerHTML={{
                        __html: transformStylesInRichText(title),
                      }}
                    />
                  )}
                </>
              )}

              {descriptionEnhanced ? (
                <div className={styles.description}>
                  {richTextify(descriptionEnhanced)}
                </div>
              ) : (
                description && <p>{description}</p>
              )}
              {subDescription && (
                <RichTextFullWidth
                  content={subDescription?.content}
                  contentTinyMce={subDescription?.contentTinyMce}
                  addBorder={false}
                />
              )}
              {hasAnyCta && (
                <div
                  className={clsx(
                    styles["cta-wrapper"],
                    "items-center gap-6 md:pr-6 xl:pr-0 mt-8",
                    hasVideoCta ? "lg:mt-32" : "lg:mt-16",
                    hideLine ? "md:flex md:mb-16" : "lg:flex"
                  )}
                >
                  {hasVideoCta && (
                    <Cta
                      onClick={handleOpenLightbox}
                      type="tertiary"
                      url={""}
                      linkText={videoLinkText}
                      openInNewTab={false}
                      icon="icon-play"
                      btnTheme="play"
                      customClass="icon-play"
                    />
                  )}
                  {hasPrimaryCta &&
                    (ctaLinkAppearance &&
                    ctaLinkAppearance.includes("primary") ? (
                      <Cta
                        type={ctaLinkAppearance}
                        light={!isLight}
                        {...ctaLink}
                      />
                    ) : (
                      <Cta type="tertiary" {...ctaLink} icon="icon-arrow" />
                    ))}
                  {hasSecondaryCta && (
                    <Cta {...ctaSecondaryLink} type="secondary" />
                  )}
                </div>
              )}
            </div>
          </div>
          {!isImage && !hideLine && (
            <span className={styles.line}>
              <span className={styles["inner-line"]} />
            </span>
          )}
        </div>
      </div>
      {showLightbox && lightboxVideoUrl && (
        <C46_HeaderLightBoxVideoPlayer
          url={lightboxVideoUrl}
          controls={isVimeoCode}
          onVideoClose={handleCloseLightbox}
          onVideoEnded={handleVideoEnded}
        />
      )}
    </div>
  );
};

export default HeaderBanner;
